import React from 'react'
import {
  oneOf,
  string,
  number,
  shape,
  array,
  oneOfType,
  func,
} from 'prop-types'
import DOMPurify from 'dompurify'
import featureToggles from 'feature-toggles'
import * as Component from '../ResultTile.styles'
import { BarGraph, Tooltip } from 'components/atoms'
import { useResultTileContext, useTextContext } from 'hocs'
import { findLowestContrib, getReplacedString } from 'settings/app/helpers'
import { entryPointId, mappedTreatmentScores } from 'settings/app/constants'
import { IndicatorList, QsrIndicator, WordHints } from 'components/molecules'

/**
 * factory for data rows
 * @param props
 * @returns {*}
 * @constructor
 */
const AccordionRowsFactory = (props) => {
  const { rowType, indicator, missingQsr, onClickHandler } = props
  const { is_aop, result, generalTooltips, handleCostEstimate } =
    useResultTileContext()
  const { type } = result
  const textContext = useTextContext()

  const getAccordionRow = () => {
    switch (rowType) {
      case 'pricePerHour':
        const rowClass = `no-accordion-container-row ${
          result?.services?.length > 0 ? 'top-padding' : ''
        }`
        return (
          <div
            className={ rowClass }
            data-cy='pricePerHour'
            data-cy-amount={ result.pricePerHour }
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ result.pricePerHour }>
            <div className='row-title'>
              {textContext['accordionRow.pricePerHour']}
            </div>

            <div className='row-content'>
              <strong>{result.pricePerHour}</strong>
            </div>
          </div>
        )

      case 'listDataRow':
        const renderList = result.type === 'ambulances'
        const dataRow = textContext[`resultTile.container.${ result.type }`]

        const getServicesAsString = () => {
          let services = ''
          if (Array.isArray(result.services) && result.services.length > 0) {
            if (typeof result.services[0] === 'string') {
              return result.services.join(', ')
            }
            result.services.forEach((service, index) => {
              services = `${ services }${ service.title }${
                index + 1 !== result.services.length ? ', ' : ''
              }`
            })
          }
          return services
        }

        return (
          <div
            className='no-accordion-container-row'
            data-cy={ dataRow.replace(':', '') }
            data-cy-dynamic-compare={ result.type === entryPointId.CARESUPPORT }
            data-cy-compare-value={
              result.type === entryPointId.CARESUPPORT
                ? getServicesAsString()
                : null
            }>
            <div className='row-title'>
              {dataRow}
              {/* show tooltip if emergencyLevels are not granted explicit check for 'false' - to hide tooltip if not defined (if is 'null' or 'undefined') */}
              {result.type === 'ambulances' && result.levelsGranted === false && (
                <Tooltip
                  infoIconId='emergencyLevels-infoIcon'
                  tooltipId='emergencyLevels-tooltip'
                  arrow='middle'
                  className='tooltip'
                  dialogTitle={ dataRow }>
                  {generalTooltips.emergencyLevelNoApproval}
                </Tooltip>
              )}
            </div>
            <div className='row-content'>
              {!renderList && getServicesAsString()}
              {renderList && result.levels && (
                <ul className='ambulances-levels'>
                  {result.levels.length === 0 && (
                    <p>
                      {textContext['resultTile.ambulances.noEmergencyLevels']}
                    </p>
                  )}
                  {result.levels.length > 0 &&
                    result.levels.map((level, index) => (
                      <li key={ index }>{level}</li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        )

      case 'contribution':
        const rightRowItemClass =
          result.type === entryPointId.CARESERVICES ? 'space-between' : ''
        let { contribution } = result

        // check if personalContributionShortTerm only render the lowest value
        if (contribution[0].type === 'personalContributionShortTerm') {
          contribution = [findLowestContrib(contribution)]
        }
        const getContributionTooltip = ({ resultType, contribution }) =>
          resultType === entryPointId.CARESERVICES
            ? contribution.contractId &&
              (contribution.tooltip
                ? contribution.tooltip
                : generalTooltips[contribution.type])
            : contribution.tooltip
            ? contribution.tooltip
            : generalTooltips[contribution.type]

        return contribution.map((contribution, key) => {
          let careLevelText, dataCy, hintTitle

          if (
            !featureToggles.costEstimate &&
            result.type === entryPointId.CARESERVICES
          ) {
            if (
              contribution.type === 'personalContribution' &&
              !!contribution.contractId
            ) {
              return true
            }
          }

          switch (contribution.type) {
            case 'personalContribution':
              careLevelText = `(${ textContext[`accordionRow.careLevel`] } ${
                contribution.careLevel
              })`
              dataCy = `${ contribution.type }-${ contribution.careLevel }`
              hintTitle =
                result.type === 'carehomes'
                  ? textContext[`accordionRow.${ contribution.type }.carehomes`]
                  : textContext[`accordionRow.${ contribution.type }`]
              break

            case 'personalContributionShortTerm':
              careLevelText = textContext[
                'accordionRow.personalContribution.carehomes_shortTerm'
              ]
                .replace('__SEARCH__PRICE__', contribution.amount)
                .replace('__SEARCH__CARE__LEVEL__', contribution.careLevel)
              dataCy = `personalContribution-${ contribution.careLevel }`
              break

            // daysandnightcare only
            case 'contributionDay':
            case 'contributionNight':
              careLevelText = `(${ textContext[`accordionRow.careLevel`] } ${
                contribution.careLevel
              })`
              dataCy = contribution.type
              hintTitle = textContext['accordionRow.dayAndNightCare']
                .replace(
                  '__SEARCH__TERM__TYPE__',
                  textContext[
                    `accordionRow.dayAndNightCare.${ contribution.type }`
                  ]
                )
                .replace('__SEARCH__TERM__DAYS__', contribution.days)
              if (contribution.days === 1) {
                hintTitle = hintTitle.replace('Besuchstagen', 'Besuchstag')
              }
              break
          }

          return (
            <Component.AccordionRow
              key={ key }
              data-cy={ dataCy }
              data-cy-dynamic-compare='true'
              data-cy-compare-value={ contribution.amount }
              data-cy-amount={ contribution.amount }
              data-cy-care-level={ contribution.careLevel }
              data-cy-care-days={
                result.type === entryPointId.DAYANDNIGHTCARE
                  ? contribution.days
                  : null
              }
              data-cy-contract-id={ contribution.contractId }
              changeItemSizes={ !!hintTitle && hintTitle.length > 40 }>
              {(contribution.type === 'personalContribution' ||
                contribution.type === 'contributionDay' ||
                contribution.type === 'contributionNight') && (
                <>
                  <Component.AccordionRowItem>
                    <WordHints
                      title={ hintTitle }
                      hint={ getContributionTooltip({
                        resultType: result.type,
                        contribution,
                      }) }
                    />
                  </Component.AccordionRowItem>
                  <Component.AccordionRowItem className={ rightRowItemClass }>
                    {result.type === entryPointId.CARESERVICES ? (
                      <>
                        {!contribution.contractId &&
                          textContext['accordionRow.contribution.noValues']}
                      </>
                    ) : (
                      <>
                        {contribution.careLevel ? (
                          contribution.amount.startsWith('-') ? (
                            textContext[
                              'accordionRow.contribution.negativeValue'
                            ]
                          ) : (
                            <div>
                              <strong>{contribution.amount}</strong>
                              <span>{careLevelText}</span>
                            </div>
                          )
                        ) : (
                          textContext['accordionRow.contribution.noValues']
                        )}
                      </>
                    )}
                    {result.type === entryPointId.CARESERVICES &&
                      contribution.contractId && (
                        <a
                          data-cy='personalContribution-link'
                          className='more-link'
                          target='_blank'
                          onClick={ (e) => {
                            e.preventDefault()
                            handleCostEstimate?.(contribution.contractId)
                          } }>
                          {
                            textContext[
                              'accordionRow.link.individualCostEstimate'
                            ]
                          }
                        </a>
                      )}
                  </Component.AccordionRowItem>
                </>
              )}

              {contribution.type === 'personalContributionShortTerm' && (
                <Component.AccordionRowItem className='fullSize'>
                  {careLevelText}
                </Component.AccordionRowItem>
              )}
            </Component.AccordionRow>
          )
        })

      case 'transparencyReport':
        return (
          <Component.AccordionRow
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ result.transparencyReport }
            data-cy='transparencyReport'
            data-cy-amount={ result.transparencyReport }
            changeItemSizes={
              type === entryPointId.CAREHOMES && result?.transparencyReport
            }>
            <Component.AccordionRowItem>
              <WordHints
                title={ textContext[`accordionRow.transparencyReport`] }
                hint={ generalTooltips.transparencyReport }
              />
            </Component.AccordionRowItem>
            <Component.AccordionRowItem>
              <strong>{result.transparencyReport}</strong>{' '}
              {textContext['accordionRow.transparencyReport.label']}
            </Component.AccordionRowItem>
          </Component.AccordionRow>
        )

      case 'quality':
        return (
          <Component.AccordionRow
            data-cy='quality'
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ result.treatment.cases }>
            <Component.AccordionRowItem>
              <WordHints
                title={ textContext[`accordionRow.quality`] }
                hint={ generalTooltips.quality }
              />
            </Component.AccordionRowItem>
            <Component.AccordionRowItem>
              <span
                className='indicators-representation'
                onClick={ () => onClickHandler() }>
                {textContext['accordionRow.link.quality']}
              </span>
            </Component.AccordionRowItem>
          </Component.AccordionRow>
        )

      // TBD treatment && treatmentAop could be reduced to one case with different value (markup is equals in booth cases)
      // normal treatments
      case 'treatment':
        const treatmentScoring = `(${
          mappedTreatmentScores[result.treatment.scoring]
        })`
        return (
          <Component.AccordionRow
            AccordionRowItem
            data-cy='treatment'
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ result.treatment.cases }>
            <Component.AccordionRowItem>
              <WordHints
                title={ `${
                  is_aop
                    ? textContext[
                        'compare.generalInformation.labelTreatment.stationary'
                      ]
                    : textContext['compare.generalInformation.labelTreatment']
                } (${ result.treatment.year })` }
                hint={ generalTooltips.treatmentCases }
              />
            </Component.AccordionRowItem>
            <Component.AccordionRowItem
              data-cy='treatmentcases'
              data-cy-treatmentcases-count={ result.treatment.cases }
              data-cy-treatmentcases-scoring={
                mappedTreatmentScores[result.treatment.scoring]
              }>
              <strong>{result.treatment.cases}</strong> {treatmentScoring}
              {result.treatment.treatment_tip && (
                <Tooltip
                  arrow='left'
                  className='tooltip treatment'
                  dialogTitle={ textContext['accordionRow.treatment'] }
                  infoIconId={ `info-icon-treatment-${ result.id }` }
                  tooltipId={ `tooltip-treatment-${ result.id }` }>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        result.treatment.treatment_tip
                      ),
                    }}
                  />
                </Tooltip>
              )}
            </Component.AccordionRowItem>
          </Component.AccordionRow>
        )

      // ambulant treatments (aop flag should be true)
      case 'treatment-aop':
        const treatmentAopScoring = `(${
          mappedTreatmentScores[result.treatment.cases_ambulant_scoring]
        })`
        return (
          <Component.AccordionRow
            data-cy='treatment-aop'
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ result.treatment.cases_ambulant }>
            <Component.AccordionRowItem>
              <WordHints
                title={ `${ textContext['accordionRow.treatmentAmbulant'] } (${ result.treatment.year })` }
                hint={ generalTooltips.treatmentCases }
              />
            </Component.AccordionRowItem>
            <Component.AccordionRowItem>
              <strong>{result.treatment.cases_ambulant}</strong>{' '}
              {treatmentAopScoring}
              {result.treatment.ambulant_treatment_tip && (
                <Tooltip
                  arrow='left'
                  className='tooltip treatment'
                  dialogTitle={
                    textContext['tooltip.ResultTile.AmbulantTreatmentTip']
                  }
                  infoIconId={ `info-icon-treatment-${ result.id }` }
                  tooltipId={ `tooltip-treatment-${ result.id }` }>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        result.treatment.ambulant_treatment_tip
                      ),
                    }}
                  />
                </Tooltip>
              )}
            </Component.AccordionRowItem>
          </Component.AccordionRow>
        )

      // recommendation & no peq values
      case 'peq':
      case 'noPEQ':
        // if general is undefined skip to noQsr
        if (result?.peq?.general) {
          const withAverageValue =
            result.peq.hasOwnProperty('average') &&
            result.peq.hasOwnProperty('cases') &&
            result.peq.average !== null &&
            result.peq.cases !== null

          return (
            <Component.AccordionRow
              data-cy='recommendation'
              data-cy-dynamic-compare='true'
              data-cy-compare-value={ result.peq.general }>
              <Component.AccordionRowItem>
                <WordHints
                  title={ textContext['accordionRow.recommendation'] }
                  hint={ generalTooltips?.recommendation }
                />
              </Component.AccordionRowItem>
              <Component.AccordionRowItem>
                {!withAverageValue ? (
                  <BarGraph percent={ result.peq.general } />
                ) : (
                  <div className='bar-graph-average-container'>
                    <BarGraph
                      percent={ result.peq.general }
                      showAverage={ true }
                      average={ result.peq.average }
                    />
                    <span className='average-label'>
                      bei {result.peq.cases} Patienten&shy;bewertungen
                    </span>
                  </div>
                )}
              </Component.AccordionRowItem>
            </Component.AccordionRow>
          )
        }

        return (
          <Component.AccordionRow
            data-cy='recommendation'
            data-cy-dynamic-compare='true'
            data-cy-compare-value='false'>
            <p className='message-no-peq'>
              {textContext['accordionRow.noQsr']}
            </p>
          </Component.AccordionRow>
        )

      // iqtig
      case 'iqtig':
        return result?.iqtig ? (
          <Component.AccordionRow
            data-cy='iqtig'
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ generalTooltips.iqtig }>
            <Component.AccordionRowItem>
              <WordHints
                title={ textContext['accordionRow.iqtig'] }
                hint={ generalTooltips.iqtig }
              />
            </Component.AccordionRowItem>
            <Component.AccordionRowItem>
              {result.iqtig}%{' '}
              {textContext['compare.generalInformation.iqtigIndicators']}
            </Component.AccordionRowItem>
          </Component.AccordionRow>
        ) : null

      // qsr indicator used on qsr
      case 'qsrIndicators':
        return (
          <Component.AccordionRow
            data-cy='qsr-single-indicators'
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ 'qsr-single-indicators' }>
            <Component.AccordionRowItem />
            <Component.AccordionRowItem>
              <IndicatorList qsrIndicators={ result.qsrIndicators } size={ 22 } />
            </Component.AccordionRowItem>
          </Component.AccordionRow>
        )

      case 'qsr':
        return (
          <Component.AccordionRow
            data-cy={ indicator.identifier }
            data-cy-qsr-value={ indicator.rating }
            data-cy-display-status={ indicator.status }
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ indicator.rating }>
            <Component.AccordionRowItem>
              <span>{textContext['accordionRow.qsr']}</span>
              <Tooltip
                arrow='left'
                className='tooltip'
                dialogTitle={ textContext['accordionRow.qsr'] }
                infoIconId={ `info-icon` }
                tooltipId={ `tooltip` }>
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(generalTooltips.qsr),
                  }}
                />
              </Tooltip>
            </Component.AccordionRowItem>
            <Component.AccordionRowItem>
              {indicator.status !== 1 && (
                <QsrIndicator currentLevel={ indicator.rating } />
              )}
              <p
                className={ `qsr-details ${
                  indicator.status !== 1 ? 'with-margin' : ''
                }` }>
                {indicator.text && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(indicator.text),
                    }}
                  />
                )}
                {indicator.info && (
                  <span className={ 'indicator-info' }>
                    {indicator.info}
                    {indicator.tooltip &&
                      indicator.tooltip.title &&
                      indicator.tooltip.tip && (
                        <Tooltip
                          arrow='left'
                          className='tooltip'
                          dialogTitle={ indicator.tooltip.title }
                          infoIconId={ `info-icon` }
                          tooltipId={ `tooltip` }>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(indicator.tooltip.tip),
                            }}
                          />
                        </Tooltip>
                      )}
                  </span>
                )}
              </p>
            </Component.AccordionRowItem>
          </Component.AccordionRow>
        )

      // no qsr values
      case 'noQsr':
        return (
          <Component.AccordionRow
            data-cy={ missingQsr.identifier }
            data-cy-qsr-value={ false }
            data-cy-display-status={ false }
            data-cy-dynamic-compare='true'
            data-cy-compare-value={ missingQsr.identifier }>
            <Component.AccordionRowItem>
              <span>{textContext['accordionRow.qsr']}</span>
              <Tooltip
                arrow='left'
                className='tooltip'
                dialogTitle={ textContext['accordionRow.qsr'] }
                infoIconId={ `info-icon` }
                tooltipId={ `tooltip` }>
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(generalTooltips.qsr),
                  }}
                />
              </Tooltip>
            </Component.AccordionRowItem>
            <Component.AccordionRowItem>
              <p>
                {getReplacedString({
                  search: textContext['noQsrResult.placeholder'],
                  replaceWith: missingQsr.title,
                })}
              </p>
            </Component.AccordionRowItem>
          </Component.AccordionRow>
        )

      default:
        return null
    }
  }
  return getAccordionRow()
}

AccordionRowsFactory.propTypes = {
  rowType: oneOf([
    'treatment',
    'treatment-aop',
    'peq',
    'qsr',
    'iqtig',
    'qsrIndicators',
    'noQsr',
    'noPEQ',
    'quality',
    'transparencyReport',
    'listDataRow',
    'pricePerHour',
    'contribution',
  ]).isRequired,
  // for qsr mapping (represent one qsr entity)
  indicator: shape({
    identifier: string.isRequired,
    indicators: array,
    info: oneOfType([number, string]),
    rating: number.isRequired,
    title: string.isRequired,
  }),
  // (conditional if no qsr exists)
  missingQsr: shape({
    identifier: string,
    title: string,
  }),
  // click handler
  onClickHandler: func,
}

export default AccordionRowsFactory
